import React from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import FormControl from "@mui/material/FormControl";
import IconButton from "@mui/material/IconButton";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import Typography from "@mui/material/Typography";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import {
  DEFAULT_SEARCH_PAGE_SIZE,
  SEARCH_PAGE_SIZE_OPTIONS,
  formatSearchResultsRange,
  getSearchPageCount,
  getVisibleSearchPages,
} from "./SearchResultsPaginationModel";
import useIsMobile from "../../hooks/useIsMobile";
import { MOBILE_TOUCH_MIN_PX, MOBILE_TYPE } from "../../theme/mobileLayout";

/**
 * Design-system pagination footer for Search results.
 * Desktop: left = Items per page + range; right = Previous → pages → Next.
 * Mobile: simplified Prev / range / Next (+ compact page size).
 */
export default function SearchResultsPagination({
  page = 0,
  rowsPerPage = DEFAULT_SEARCH_PAGE_SIZE,
  totalRows = 0,
  lastReachablePage = page,
  disabled = false,
  onPageChange,
  onRowsPerPageChange,
}) {
  const isMobile = useIsMobile();
  const pageCount = getSearchPageCount(totalRows, rowsPerPage);
  const reachableLast = Math.min(pageCount - 1, Math.max(page, lastReachablePage));
  const visiblePages = getVisibleSearchPages(page, pageCount, reachableLast);
  const rangeLabel = formatSearchResultsRange({ page, rowsPerPage, totalRows });
  const canGoPrev = page > 0 && !disabled;
  const canGoNext = page < reachableLast && !disabled;
  const [rangeStart, rangeTotal] = rangeLabel.includes(" of ")
    ? rangeLabel.split(" of ")
    : [rangeLabel, ""];

  if (isMobile) {
    return (
      <Box
        data-testid="search-results-pagination"
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 1,
          px: 1.5,
          py: 1.25,
          borderTop: "1px solid",
          borderColor: "divider",
          bgcolor: "background.paper",
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 0.75,
            width: "100%",
            justifyContent: "space-between",
          }}
          data-testid="mobile-search-pagination"
        >
          <IconButton
            aria-label="Previous page"
            disabled={!canGoPrev}
            onClick={() => onPageChange?.(page - 1)}
            sx={{ minWidth: MOBILE_TOUCH_MIN_PX, minHeight: MOBILE_TOUCH_MIN_PX }}
          >
            <ChevronLeftIcon />
          </IconButton>
          <Box sx={{ textAlign: "center", minWidth: 0 }}>
            <Typography sx={{ ...MOBILE_TYPE.secondary, fontWeight: 700, color: "text.primary" }}>
              {pageCount > 0 ? `Page ${page + 1} of ${pageCount}` : "Page 1"}
            </Typography>
            <Typography
              data-testid="search-results-range"
              sx={{ ...MOBILE_TYPE.helper, color: "text.secondary" }}
            >
              {rangeStart}{rangeTotal ? ` of ${rangeTotal}` : ""}
            </Typography>
          </Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            <FormControl size="small" disabled={disabled}>
              <Select
                value={rowsPerPage}
                onChange={(event) => onRowsPerPageChange?.(Number(event.target.value))}
                inputProps={{ "aria-label": "Items per page" }}
                sx={{
                  minWidth: 64,
                  fontSize: 12,
                  borderRadius: "10px",
                  "& .MuiSelect-select": { py: 0.75, px: 1 },
                }}
              >
                {SEARCH_PAGE_SIZE_OPTIONS.map((option) => (
                  <MenuItem key={option} value={option} sx={{ fontSize: 13 }}>
                    {option}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <IconButton
              aria-label="Next page"
              disabled={!canGoNext}
              onClick={() => onPageChange?.(page + 1)}
              sx={{ minWidth: MOBILE_TOUCH_MIN_PX, minHeight: MOBILE_TOUCH_MIN_PX }}
            >
              <ChevronRightIcon />
            </IconButton>
          </Box>
        </Box>
      </Box>
    );
  }

  return (
    <Box
      data-testid="search-results-pagination"
      sx={{
        display: "flex",
        flexWrap: "wrap",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 1.5,
        px: 2,
        py: 1.25,
        borderTop: "1px solid",
        borderColor: "divider",
        bgcolor: "background.paper",
      }}
    >
      <Box
        data-testid="search-results-pagination-meta"
        sx={{ display: "flex", alignItems: "center", gap: 1.5, minWidth: 0 }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Typography variant="body2" color="text.secondary" sx={{ fontSize: 13, whiteSpace: "nowrap" }}>
            Items per page:
          </Typography>
          <FormControl size="small" disabled={disabled}>
            <Select
              value={rowsPerPage}
              onChange={(event) => onRowsPerPageChange?.(Number(event.target.value))}
              inputProps={{ "aria-label": "Items per page" }}
              sx={{
                minWidth: 72,
                fontSize: 13,
                borderRadius: "10px",
                "& .MuiSelect-select": {
                  py: 0.75,
                  px: 1.25,
                },
              }}
            >
              {SEARCH_PAGE_SIZE_OPTIONS.map((option) => (
                <MenuItem key={option} value={option} sx={{ fontSize: 13 }}>
                  {option}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>

        <Typography
          variant="body2"
          color="text.secondary"
          data-testid="search-results-range"
          sx={{ fontSize: 13, whiteSpace: "nowrap" }}
        >
          <Box component="span" sx={{ fontWeight: 600, color: "text.primary" }}>
            {rangeStart}
          </Box>
          {rangeTotal ? ` of ${rangeTotal}` : null}
        </Typography>
      </Box>

      <Box
        data-testid="search-results-pagination-nav"
        sx={{ display: "flex", alignItems: "center", gap: 0.75, ml: "auto" }}
      >
        <Button
          size="small"
          color="inherit"
          disabled={!canGoPrev}
          onClick={() => onPageChange?.(page - 1)}
          startIcon={<ChevronLeftIcon fontSize="small" />}
          sx={{
            textTransform: "none",
            fontSize: 13,
            fontWeight: 500,
            minWidth: 0,
            px: 1,
            color: "text.primary",
            "&.Mui-disabled": { color: "text.disabled" },
          }}
        >
          Previous
        </Button>

        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
          {visiblePages.map((pageIndex, position) => {
            if (pageIndex == null) {
              return (
                <Typography
                  key={`ellipsis-${position}`}
                  variant="body2"
                  color="text.secondary"
                  sx={{ px: 0.5, fontSize: 13 }}
                  aria-hidden
                >
                  …
                </Typography>
              );
            }
            const selected = pageIndex === page;
            return (
              <Button
                key={pageIndex}
                size="small"
                disabled={disabled}
                onClick={() => onPageChange?.(pageIndex)}
                aria-current={selected ? "page" : undefined}
                sx={{
                  minWidth: 32,
                  height: 32,
                  px: 0.75,
                  borderRadius: "8px",
                  textTransform: "none",
                  fontSize: 13,
                  fontWeight: selected ? 600 : 500,
                  color: "text.primary",
                  border: "1px solid",
                  borderColor: selected ? "divider" : "transparent",
                  bgcolor: selected ? "background.paper" : "transparent",
                  "&:hover": {
                    bgcolor: "action.hover",
                    borderColor: selected ? "divider" : "transparent",
                  },
                }}
              >
                {pageIndex + 1}
              </Button>
            );
          })}
        </Box>

        <Button
          size="small"
          color="inherit"
          disabled={!canGoNext}
          onClick={() => onPageChange?.(page + 1)}
          endIcon={<ChevronRightIcon fontSize="small" />}
          sx={{
            textTransform: "none",
            fontSize: 13,
            fontWeight: 500,
            minWidth: 0,
            px: 1,
            color: "text.primary",
            "&.Mui-disabled": { color: "text.disabled" },
          }}
        >
          Next
        </Button>
      </Box>
    </Box>
  );
}
